import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/honest-answers/not-a-fit");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/honest-answers/not-a-fit")({
  head: () => ({
    meta: [
      {
        title:
          "When Colorado Web Impressions Is NOT the Best Fit (And Who Is)",
      },
      {
        name: "description",
        content:
          "We turn down work every month. Here&rsquo;s exactly when we&rsquo;ll tell you to hire someone else — and who to call instead.",
      },
      {
        property: "og:title",
        content: "When we're not the right fit for your business",
      },
      {
        property: "og:description",
        content:
          "Honest disqualifiers — and where to go instead if we&rsquo;re not your match.",
      },
      { property: "og:url", content: `${SITE_URL}/honest-answers/not-a-fit` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Colorado Web Impressions",
        description: ARTICLE?.desc ?? "Honest answers from a 25-year Colorado agency.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/honest-answers/not-a-fit" }],
  }),
  component: NotAFitArticle,
});

const cases = [
  {
    title: "You&rsquo;re a brand-new business with no revenue yet.",
    fit: "Skip us.",
    why: "If you haven&rsquo;t made your first sale, the smartest dollar you can spend is on getting customers any way possible — door knocking, referrals, free Google Business Profile, Nextdoor. A $5K website is the wrong move at month one.",
    instead: "Set up a free Google Business Profile, post on Nextdoor and local Facebook groups, and reinvest your first 10 jobs into a real site.",
  },
  {
    title: "Your budget is under $500/month and you need leads tomorrow.",
    fit: "Skip us.",
    why: "Real SEO takes 4–8 months to compound. Real ads need budget to test. If you have $400/month total and need calls this week, you need leadgen platforms (Angi, Thumbtack, Service Direct) — not us.",
    instead: "Start with one local lead-gen platform plus a polished free Google Business Profile while you build cash flow.",
  },
  {
    title: "You want a one-time SEO &ldquo;package&rdquo; with no ongoing work.",
    fit: "Skip us.",
    why: "SEO doesn&rsquo;t work like that. Anyone who sells you &ldquo;one-time SEO for $1,500 and you&rsquo;ll rank forever&rdquo; is lying. We won&rsquo;t take your money for something we know won&rsquo;t work.",
    instead: "Either commit to ongoing SEO (3–6 months minimum) or skip SEO entirely and run Google Ads instead.",
  },
  {
    title: "You operate outside Colorado.",
    fit: "Skip us.",
    why: "We only work with Colorado businesses. We know the Front Range, the mountain markets, the seasonality, the competition. We&rsquo;re not the right call for a Phoenix HVAC company.",
    instead: "Find a local agency in your state. Same logic — they&rsquo;ll know your market the way we know ours.",
  },
  {
    title: "You expect us to write content without your input.",
    fit: "Tough fit.",
    why: "Great content needs your stories, your photos, your job-site experiences. If you&rsquo;re not willing to spend 30 minutes a month on a recorded interview or a few photos from a job site, content marketing won&rsquo;t work — for us or anyone else.",
    instead: "Skip content marketing. Focus your budget on paid ads where your direct involvement matters less.",
  },
  {
    title: "You want us to manage Facebook lead-form ads only.",
    fit: "Tough fit.",
    why: "Facebook lead ads can work, but it&rsquo;s a niche specialty. We&rsquo;re built for search-intent (Google) marketing. We&rsquo;ll do a worse job than a Facebook-only specialist would.",
    instead: "Find a paid-social specialist agency or freelancer who does FB lead-gen all day, every day.",
  },
  {
    title: "You&rsquo;re looking for the cheapest possible option.",
    fit: "Skip us.",
    why: "We&rsquo;re not the cheapest. We&rsquo;re fair, but we&rsquo;re not bargain-bin. If price is your #1 filter, you&rsquo;ll be unhappy with us — even if we deliver. Better to be honest about that now.",
    instead: "Try Wix, GoDaddy, or a freelancer on Upwork. You&rsquo;ll save money up front. Just understand the trade-offs (we covered them in our pricing article).",
  },
  {
    title: "You want to micromanage every pixel.",
    fit: "Tough fit.",
    why: "If you want to approve every comma, every shade of blue, and every word of every blog post — you&rsquo;ll be frustrated by our pace, and we&rsquo;ll be frustrated trying to deliver results inside your approval bottleneck.",
    instead: "Hire an in-house marketing person or a freelance designer you can direct daily. Or hire us, then back off and trust the process — we&rsquo;ll show you wins in 90 days.",
  },
];

function NotAFitArticle() {
  return (
    <ArticleLayout
      eyebrow="Disqualifier"
      read="6 min read"
      title="When Colorado Web Impressions is NOT the best fit for your business"
      intro="We turn down work every single month. It&rsquo;s the most counterintuitive thing we do — and it&rsquo;s why our clients trust us. If any of these sound like you, save us both time and skip the call."
      path="/honest-answers/not-a-fit"
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Honest Answers", to: "/honest-answers" },
        { label: "When We're Not a Fit" },
      ]}
      faqs={[
        { q: "Why would a marketing agency tell me we're not a fit?", a: "Because forcing a bad fit wastes everyone's time and money. If our process doesn't match your goals, budget, or industry, we'll tell you straight and recommend who is a fit." },
        { q: "What kinds of businesses are not a good fit for your agency?", a: "Businesses looking for the cheapest possible option, anyone wanting guaranteed rankings, or industries we don't have experience in. Honest fit beats forced fit every time." },
        { q: "What should I do if multiple agencies say we're not a fit?", a: "Listen. It usually means your expectations, budget, or timeline need to be recalibrated — not that every agency is wrong." },
      ]}
    >
      <p>
        Most agencies will take any check that clears. We won&rsquo;t. Bad-fit clients are
        miserable for everyone — they get worse results, write worse reviews, and burn out our
        team. We&rsquo;d rather refer you to someone better suited than try to force a fit.
      </p>

      <div className="not-prose my-10 space-y-5">
        {cases.map((c) => (
          <div key={c.title} className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-destructive/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-destructive">
                {c.fit}
              </div>
            </div>
            <h3
              className="mt-3 text-lg font-bold text-foreground"
              dangerouslySetInnerHTML={{ __html: c.title }}
            />
            <p
              className="mt-2 text-sm leading-relaxed text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: c.why }}
            />
            <div className="mt-4 rounded-lg border border-brand-gold/30 bg-brand-gold/5 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
                Do this instead
              </div>
              <p
                className="mt-1.5 text-sm font-medium text-foreground/90"
                dangerouslySetInnerHTML={{ __html: c.instead }}
              />
            </div>
          </div>
        ))}
      </div>

      <h2>When we ARE a great fit</h2>
      <p>
        If you read all that and none of it applies, here&rsquo;s when we&rsquo;re probably the
        right call:
      </p>
      <ul>
        <li>You&rsquo;re a Colorado-based service business doing $250K+ in annual revenue.</li>
        <li>You can invest $1,500–$5,000/month in marketing for at least 6 months.</li>
        <li>You want a real partner — not a vendor. You&rsquo;re willing to be involved.</li>
        <li>You value transparency, ownership, and being treated like an adult.</li>
        <li>You&rsquo;re tired of being burned by agencies that overpromise and disappear.</li>
      </ul>

      <p>
        Sound like you? Take the{" "}
        <Link to="/are-we-a-fit" className="font-bold text-brand-gold-deep underline">
          2-minute fit quiz
        </Link>{" "}
        to confirm, or{" "}
        <Link to="/contact" className="font-bold text-brand-gold-deep underline">
          book a free call
        </Link>{" "}
        and we&rsquo;ll figure it out together.
      </p>
    </ArticleLayout>
  );
}
